import { useState } from 'react';
import classNames from 'classnames';

import { DataTable, Paginator, Chip, FilterBar } from 'components';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import { displayDateTime, statusColor } from './format';
import { DirectClaim } from './DirectClaims.types';
import { DirectClaimsFilter } from './DirectClaims';

import './direct-claims.scss';

// HP is GlobeMed's code for hospital (inpatient) claims.
const CATEGORY_LABELS: Record<string, string> = {
  ALL: 'All categories',
  OUT: 'Outpatient',
  HP: 'Inpatient',
};

export const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(
  ([value, label]) => ({ label, value })
);

interface DirectClaimsUiProps {
  claims: DirectClaim[];
  totalRecords: number;
  loading: boolean;
  errorMessage: string;
  filter: DirectClaimsFilter;
  startDate: Date | null;
  endDate: Date | null;
  page: { first: number; rows: number };
  statusOptions: { label: string; value: string }[];
  handlers: {
    handleSearchChange: (name: string, value: string) => void;
    handleSubmitSearch: () => void;
    handleStartDateChange: (event: any) => void;
    handleEndDateChange: (event: any) => void;
    handleCategoryChange: (value: string) => void;
    handleStatusChange: (value: string) => void;
    handlePageChange: (event: any) => void;
  };
}

// Two claims can share every visible field (same member, minute and service),
// so the row key is built from enough of them to stay unique on a page.
const rowKey = (claim: DirectClaim, index: number) =>
  `${claim.memberId}-${claim.serviceDate}-${claim.serviceType}-${index}`;

function memberBody(claim: DirectClaim) {
  return (
    <div className="direct-claims__member">
      <span className="direct-claims__member-name">
        {claim.memberName || '-'}
      </span>
      <span className="direct-claims__muted">{claim.memberId}</span>
    </div>
  );
}

function statusBody(claim: DirectClaim) {
  if (!claim.status) return '-';
  return <Chip label={claim.status} color={statusColor(claim.status)} />;
}

function diagnosisBody(claim: DirectClaim) {
  const [primary, ...others] = claim.diagnoses;
  if (!primary) return '-';

  return (
    <span>
      <strong>{primary.code}</strong> {primary.description}
      {others.length > 0 && (
        <span className="direct-claims__muted"> +{others.length} more</span>
      )}
    </span>
  );
}

function remarksBody(claim: DirectClaim) {
  const count = claim.rejectionReasons.length;
  if (count === 0) return '-';

  return (
    <span
      className="direct-claims__remarks"
      title={claim.rejectionReasons.join('\n')}
    >
      {claim.rejectionReasons[0]}
      {count > 1 && (
        <span className="direct-claims__muted"> +{count - 1} more</span>
      )}
    </span>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="direct-claims__detail">
      <span className="direct-claims__detail-label">{label}</span>
      <span>
        {value === null || value === undefined || value === '' ? '-' : value}
      </span>
    </div>
  );
}

/*
 * What the row does not already show. Coverage class and policy holder have
 * columns of their own, and the row carries the first diagnosis and remark, so
 * the full lists appear only when there is more than one.
 */
export function ClaimExpansion(claim: DirectClaim) {
  const hasMoreDiagnoses = claim.diagnoses.length > 1;
  const hasMoreRemarks = claim.rejectionReasons.length > 1;
  const hasLists = hasMoreDiagnoses || hasMoreRemarks;

  return (
    <div
      className={classNames('direct-claims__expansion', {
        'direct-claims__expansion--single': !hasLists,
      })}
    >
      <div className="direct-claims__details">
        <Detail label="Provider" value={claim.providerName} />
        <Detail label="Carrier" value={claim.carrier} />
        <Detail
          label="Contract"
          value={
            claim.contractNumber
              ? `${claim.contractNumber}${
                  claim.contractDescription
                    ? `, ${claim.contractDescription}`
                    : ''
                }`
              : null
          }
        />
        <Detail label="Co-insurance" value={claim.coInsurance} />
        <Detail label="Chronic" value={claim.chronic ? 'Yes' : 'No'} />
        <Detail label="Claim number" value={claim.claimNumber} />
        <Detail label="Report date" value={displayDateTime(claim.reportDate)} />
        <Detail label="Received" value={displayDateTime(claim.receivedAt)} />
        <Detail label="Sent" value={displayDateTime(claim.sentAt)} />
        {claim.requestedLengthOfStay !== null && (
          <Detail label="Requested stay" value={claim.requestedLengthOfStay} />
        )}
        {claim.approvedLengthOfStay !== null && (
          <Detail label="Approved stay" value={claim.approvedLengthOfStay} />
        )}
        {claim.dischargeDate && (
          <Detail
            label="Discharge date"
            value={displayDateTime(claim.dischargeDate)}
          />
        )}
      </div>

      {hasLists && (
        <div className="direct-claims__lists">
          {hasMoreDiagnoses && (
            <div>
              <h4 className="direct-claims__list-title">Diagnoses</h4>
              <ul className="direct-claims__list">
                {claim.diagnoses.map((diagnosis) => (
                  <li key={`${diagnosis.code}-${diagnosis.rank}`}>
                    <strong>{diagnosis.code}</strong> {diagnosis.description}
                    {diagnosis.rank && (
                      <span className="direct-claims__muted">
                        {' '}
                        ({diagnosis.rank})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasMoreRemarks && (
            <div>
              <h4 className="direct-claims__list-title">Remarks</h4>
              <ul className="direct-claims__list">
                {claim.rejectionReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COLUMNS = [
  { expander: true, field: 'expander', style: { width: '3rem' } },
  { header: 'Member', field: 'memberName', sortable: true, body: memberBody },
  {
    header: 'Service date',
    field: 'serviceDate',
    sortable: true,
    body: (claim: DirectClaim) => displayDateTime(claim.serviceDate),
  },
  { header: 'Service type', field: 'serviceType', sortable: true },
  {
    header: 'Category',
    field: 'category',
    sortable: true,
    body: (claim: DirectClaim) =>
      (claim.category && CATEGORY_LABELS[claim.category]) ||
      claim.category ||
      '-',
  },
  { header: 'Status', field: 'status', sortable: true, body: statusBody },
  { header: 'Diagnosis', field: 'diagnoses', body: diagnosisBody },
  { header: 'Remarks', field: 'rejectionReasons', body: remarksBody },
  { header: 'Coverage class', field: 'coverageClass', sortable: true },
  { header: 'Policy holder', field: 'policyHolder', sortable: true },
];

// The category column only earns its place when the rows can be of either.
export const columnsFor = (category: string) =>
  category === 'ALL'
    ? COLUMNS
    : COLUMNS.filter((column) => column.field !== 'category');

export default function DirectClaimsUi({
  claims,
  totalRecords,
  loading,
  errorMessage,
  filter,
  startDate,
  endDate,
  page,
  statusOptions,
  handlers,
}: DirectClaimsUiProps) {

  const [expandedRows, setExpandedRows] = useState<any>(null);

  const rows = claims
    .map((claim, index) => ({ ...claim, key: rowKey(claim, index) }))
    .slice(page.first, page.first + page.rows);

  return (
    <div className="direct-claims">
      <FilterBar
        search={{
          inputs: [
            {
              name: 'referenceNumber',
              placeholder: 'Reference #',
              value: filter.referenceNumber,
            },
            {
              name: 'referenceType',
              placeholder: 'Reference type',
              value: filter.referenceType,
            },
            {
              name: 'middleName',
              placeholder: 'Middle name',
              value: filter.middleName,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSubmitSearch,
          width: 12,
        }}
        filters={[
          {
            kind: 'date',
            name: 'startDate',
            placeholder: 'Service date from',
            selectionMode: 'single',
            // Day first, matching the dates in the table.
            dateFormat: 'dd/mm/yy',
            value: startDate,
            maxDate: endDate ?? undefined,
            onChange: handlers.handleStartDateChange,
            width: 3,
          },
          {
            kind: 'date',
            name: 'endDate',
            placeholder: 'Service date to',
            selectionMode: 'single',
            dateFormat: 'dd/mm/yy',
            value: endDate,
            minDate: startDate ?? undefined,
            onChange: handlers.handleEndDateChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'category',
            placeholder: 'Category',
            value: filter.category,
            options: CATEGORY_OPTIONS,
            onChange: (event: any) =>
              handlers.handleCategoryChange(event.value),
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'status',
            placeholder: 'Status',
            value: filter.status,
            options: statusOptions,
            onChange: (event: any) => handlers.handleStatusChange(event.value),
            width: 3,
          },
        ]}
      />

      {errorMessage && (
        <p className="direct-claims__error" role="alert">
          {errorMessage}
        </p>
      )}

      {loading && (
        <p className="direct-claims__muted direct-claims__loading">
          Loading claims from GlobeMed, this can take a few seconds.
        </p>
      )}

      <DataTable
        value={loading ? [] : rows}
        columns={columnsFor(filter.category) as any}
        dataKey="key"
        totalRecords={loading ? 0 : totalRecords}
        expandedRows={expandedRows}
        onRowToggle={(event: any) => setExpandedRows(event.data)}
        rowExpansionTemplate={ClaimExpansion}
        emptyMessage={
          loading
            ? 'Loading...'
            : 'No claims for these filters. Try a wider date range or another category.'
        }
        stripedRows
      />

      {!loading && totalRecords > 0 && (
        <Paginator
          first={page.first}
          rows={page.rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handlers.handlePageChange}
        />
      )}
    </div>
  );
}
